/**
 * Display-only currency utilities for dashboard aggregation views.
 *
 * These use approximate FX rates from entities.js (FX_TO_EUR) for display
 * aggregation ONLY. They are NOT used for financial computation, reconciliation,
 * or ledger posting. The rates are frontend-only approximations.
 *
 * Single source of truth: DashboardPage, GroupJurisdictionTab, and
 * ByJurisdictionView import from here.
 */

import { FX_TO_EUR } from './entities';

var CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', DKK: 'kr' };

/**
 * Cross-currency conversion using FX_TO_EUR as base.
 * Display-only; not for financial computation.
 */
export function convertCurrencyDisplay(amount, from, to) {
  if (from === to) return amount;
  var inEUR = amount * (FX_TO_EUR[from] || 1);
  var targetRate = FX_TO_EUR[to] || 1;
  return inEUR / targetRate;
}

/**
 * Convert a { GBP: 1000, EUR: 500, ... } map into a single target currency.
 * Display-only.
 */
export function convertBalancesDisplay(amounts, targetCurrency) {
  return Object.entries(amounts).reduce(
    function (sum, pair) { return sum + convertCurrencyDisplay(pair[1], pair[0], targetCurrency); },
    0
  );
}

/**
 * Parse a backend rate map ({ "EUR_GBP": "0.857", ... }) keyed "<CUR>_<SUFFIX>"
 * into { CUR: number } (1 unit of CUR = N of the suffix currency). Non-matching
 * keys and non-positive rates are skipped.
 */
function parseSuffixRateMap(raw, suffix) {
  var out = {};
  var re = new RegExp('^([A-Z]{3})_' + suffix + '$');
  Object.entries(raw || {}).forEach(function (pair) {
    var m = pair[0].match(re);
    if (!m) return;
    var r = parseFloat(pair[1]);
    if (r > 0) out[m[1]] = r;
  });
  return out;
}

/**
 * Build a balance converter backed by LIVE full-precision backend rates (the
 * same get_rate_safe rates the bank_balance_positions predicate uses), replacing
 * the hardcoded FX_TO_EUR approximation for the By-Jurisdiction cards.
 *
 * Directional by design: GBP-target cards convert via the "<cur>_GBP" rates and
 * EUR-target cards via the "<cur>_EUR" rates, so each card matches the predicate
 * to the penny (a GBP-pivot would reintroduce a reciprocal-rounding gap on
 * EUR-target cards). A currency with no live rate is EXCLUDED from the total
 * (surfaced-not-zeroed), never summed as the target one-for-one (the old bug).
 *
 * @param {object} fxRatesSpot  backend fx_rates_spot, a single map carrying both
 *   directions at full precision ({ "EUR_GBP": "0.8572", "GBP_EUR": "1.1666" }).
 * @returns {(amounts: object, targetCurrency: string) => number}
 */
export function makeLiveBalanceConverter(fxRatesSpot) {
  var maps = {
    GBP: Object.assign({ GBP: 1 }, parseSuffixRateMap(fxRatesSpot, 'GBP')),
    EUR: Object.assign({ EUR: 1 }, parseSuffixRateMap(fxRatesSpot, 'EUR')),
  };
  return function (amounts, targetCurrency) {
    var rateMap = maps[targetCurrency];
    return Object.entries(amounts || {}).reduce(function (sum, pair) {
      var cur = pair[0];
      var amt = pair[1];
      if (cur === targetCurrency) return sum + amt;
      var rate = rateMap ? rateMap[cur] : undefined;
      if (rate == null) return sum; // no live rate: excluded, not summed at 1
      return sum + amt * rate;
    }, 0);
  };
}

/**
 * Format a number as a compact display currency string (no decimals).
 * e.g. formatCurrencyShort(12500, 'GBP') => '£12,500'
 */
export function formatCurrencyShort(value, currency) {
  var cur = currency || 'GBP';
  var symbol = CURRENCY_SYMBOLS[cur] || cur;
  return symbol + Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export { CURRENCY_SYMBOLS };
